import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { approveInvoiceLineSchema, ApproveInvoiceLineSchemaInput, ApproveInvoiceLineSchemaOutput } from "view/invoicing/utils/invoicing.schema";

import { ApproveInvoiceLinesDocument } from "__generated__/view/invoicing/graphql/invoicing.mutations.generated";

interface ApproveInvoiceLineDialogProps {
	open: boolean;
	onClose: () => void;
	invoiceLineIds: number[];
	initialReference?: string | null;
	refetch: () => void;
}

const ApproveInvoiceLineDialog = ({ open, onClose, invoiceLineIds, initialReference, refetch }: ApproveInvoiceLineDialogProps) => {

	const form = useForm<ApproveInvoiceLineSchemaInput, unknown, ApproveInvoiceLineSchemaOutput>({
		resolver: zodResolver(approveInvoiceLineSchema),
		defaultValues: {
			reference: "",
		},
	});

	const { control, handleSubmit, reset } = form;

	const [ approveInvoiceLines, { loading } ] = useMutation(ApproveInvoiceLinesDocument);

	const handleOnClose = () => {
		onClose();

		reset({
			reference: "",
		});
	};

	const save = (data: ApproveInvoiceLineSchemaOutput) => {
		approveInvoiceLines({
			variables: {
				invoiceLineIds: invoiceLineIds,
				reference: data.reference,
			},
		}).then(() => {
			handleOnClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				reference: initialReference ?? "",
			});
		}
	}, [ open, initialReference ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "INVOICE_LINES_APPROVE" })}
			form="approveInvoiceLineForm"
			loading={loading}
			confirmText={intl.formatMessage({ id: "INVOICE_LINES_APPROVE" })}
			maxWidth="xs">
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="approveInvoiceLineForm">
				<FormGroup label={intl.formatMessage({ id: "INVOICE_REFERENCE" })} overflow="hidden" required>
					<Controller
						name="reference"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} value={field.value ?? ""} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default ApproveInvoiceLineDialog;
